import { Box, useBreakpointValue, Stack, useColorModeValue, Center } from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useCallback } from "react";
import { TabTitle } from "screens/common/components/TabTitle";
import { PurchasedReportsWatchList } from "./PurchasedContent/PurchasedReportsWatchList";
import { filterCollectionsByType, useCollections, useConfigMap, useHomeLoadingState, useMenuConfig, useDashboardMetricsData } from "hooks";
import { SettingsProviderContext } from "screens/panels/settings/SettingsProvider";
import { useConversationContext } from "screens/thread/ConversationContext";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { HomeSkeleton } from "./HomeSkeleton";
import { ScrollingTickers } from "screens/common/components/ScrollingTickers";
import groupBy from "lodash/groupBy";
import { ProjectWatchListGrid } from "./ProjectWatchList/ProjectWatchListGrid";
import { NewsTilesSection } from "./NewsTilesSection";
import { HomeQuadrantChart } from "./HomeQuadrantChart";
import { DealWatchListGrid } from "./DealWatchList/DealWatchListGrid";
import { generateQuadrantData } from "../collections/chartComponents/data/quadrantData";

export const HomeContent = () => {
  const { setFilteredProjectsIds } = useConversationContext();
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { fallback: "md", ssr: false });
  const { setHeaderText } = useContext(SettingsProviderContext);
  const { onConversationClose } = useConversationContext();
  const { setActionView } = useAddToCharliContext();
  const bgSectionsColor = useColorModeValue("#fbfbfb", "gray.800");
  const collections = useCollections();
  const configMap = useConfigMap();
  const menuConfig = useMenuConfig(configMap, "all", false);
  const { isLoading: isHomeLoading } = useHomeLoadingState();

  const filterCollectionsByPortfolio = useCallback(
    (isFiltered: boolean) => {
      const portfolioCategory = menuConfig.filter((menuItem) => !!menuItem.config.portfolioProjectType);
      const portfolioCategoryTypes = portfolioCategory.map((menuItem) => menuItem.config.collectionType);

      if (!collections) {
        return [];
      }

      return collections.filter((collection) =>
        isFiltered
          ? portfolioCategoryTypes.includes(collection.collectionType)
          : !portfolioCategoryTypes.includes(collection.collectionType)
      );
    },
    [collections, menuConfig]
  );

  useEffect(() => {
    onConversationClose();
    setActionView(undefined);
    setHeaderText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    const newFilteredProjects = filterCollectionsByPortfolio(false);
    setFilteredProjectsIds(newFilteredProjects.map((project) => project.id));
  }, [filterCollectionsByPortfolio, setFilteredProjectsIds]);

  const filteredCollections = useMemo(
    () =>
      filterCollectionsByType(collections, "due_diligence")
        .filter((collection) => !collection.name.toLowerCase().includes("new project"))
        .slice(0, 5),
    [collections]
  );
  const filterCollectionGroups = useMemo(() => {
    const groupedCollections = groupBy(filteredCollections, (collection) => collection.projectGroupId || collection.id);
    return Object.values(groupedCollections).map((group) => {
      const groupCount = group.length;
      const groupName = group[0].name;
      const groupType = group[0].collectionType;
      const ticker = group[0].ticker || "";

      return {
        id: group[0].id,
        groupName,
        groupType,
        groupCount,
        ticker,
      };
    });
  }, [filteredCollections]);
  const collectionIds = useMemo(() => filteredCollections.map((collection) => collection.id), [filteredCollections]);

  const { quadrantData } = useDashboardMetricsData("pi_quadrant_index", collectionIds);
  const legacyQuadrantData = useMemo(() => generateQuadrantData(filteredCollections), [filteredCollections]);
  const quadrantDataToUse = useMemo(() => {
    const mergedDataMap = new Map();
    // Add legacy data first (lower priority)
    if (legacyQuadrantData && legacyQuadrantData.length > 0) {
      legacyQuadrantData.forEach((item) => {
        if (item.projectId) {
          mergedDataMap.set(item.projectId, item);
        }
      });
    }
    // Add quadrant data (higher priority - will overwrite legacy data for same projectId)
    if (quadrantData && quadrantData.length > 0) {
      quadrantData.forEach((item) => {
        if (item.projectId) {
          mergedDataMap.set(item.projectId, item);
        }
      });
    }

    return Array.from(mergedDataMap.values());
  }, [quadrantData, legacyQuadrantData]);

  if (isHomeLoading) {
    return <HomeSkeleton />;
  }

  if (isMobile) {
    return (
      <>
        <Box height="100%">
          <Center py="1rem">
            <ScrollingTickers />
          </Center>
          <Stack
            pb="2rem"
            px=".5rem"
            width="100%"
            direction="column"
            justifyContent="space-between"
            bgColor={bgSectionsColor}
            spacing="1rem">
            <HomeQuadrantChart quadrantData={quadrantDataToUse} />
            <DealWatchListGrid />
            <PurchasedReportsWatchList />
            <ProjectWatchListGrid filterCollectionGroups={filterCollectionGroups} />
            <NewsTilesSection />
          </Stack>
        </Box>
        <TabTitle title="Charli > Home" />
      </>
    );
  }

  return (
    <>
      <Box height="100%">
        <Center py={isTablet ? "1rem" : "2rem"}>
          <ScrollingTickers />
        </Center>
        <Stack
          pb="2rem"
          pl={isTablet ? ".5rem" : "1rem"}
          pr={isTablet ? ".5rem" : "1rem"}
          width="100%"
          direction="row"
          justifyContent="space-between"
          bgColor={bgSectionsColor}
          spacing={isTablet ? "1rem" : "2rem"}>
          <Stack width={"100%"} spacing={isTablet ? "1rem" : "2rem"} height={"100%"}>
            <HomeQuadrantChart quadrantData={quadrantDataToUse} />
            <ProjectWatchListGrid filterCollectionGroups={filterCollectionGroups} />
          </Stack>
          <Stack width="100%" spacing="2rem" height={"100%"}>
            <DealWatchListGrid />
            <PurchasedReportsWatchList />
            <Stack width="100%" spacing="1rem" height={"100%"}>
              <NewsTilesSection />
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <TabTitle title="Charli > Home" />
    </>
  );
};
